import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  CheckCircle2, 
  Clock, 
  FileText, 
  PenTool, 
  Download,
  AlertCircle,
  User
} from 'lucide-react';
import { cn } from '../../lib/utils';

const BriefView = ({ brief, project, onSign, isAgency, isClient, theme }) => {
  const isBothSigned = brief.client_signed && brief.agency_signed;
  const canSign = (isAgency && !brief.agency_signed) || (isClient && !brief.client_signed);

  // Group fields by section (based on label patterns)
  const groupedFields = brief.fields?.reduce((acc, field) => {
    // Simple grouping - in a real app, you'd have section info in the field
    const section = 'Détails du brief';
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {}) || {};

  const formatFieldValue = (field) => {
    if (field.field_type === 'checkbox') {
      return field.value ? '✓ Oui' : '✗ Non';
    }
    return field.value || '-';
  };

  return (
    <div className="space-y-6">
      {/* Brief Status Card */}
      <Card className="card">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: isBothSigned ? '#dcfce7' : `${theme?.colors?.alert}20` }}
              >
                {isBothSigned ? (
                  <CheckCircle2 className="w-7 h-7 text-green-600" />
                ) : (
                  <Clock className="w-7 h-7" style={{ color: theme?.colors?.alert }} />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: theme?.colors?.primary }}>
                  {isBothSigned ? 'Brief validé' : 'Brief en attente de signatures'}
                </h3>
                <p className="text-sm text-[#64748b]">
                  {isBothSigned 
                    ? 'Le brief a été signé par les deux parties' 
                    : 'Le brief doit être signé par l\'agence et le client pour démarrer'
                  }
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Télécharger PDF
              </Button>
              {canSign && (
                <Button 
                  onClick={onSign}
                  size="sm"
                  style={{ backgroundColor: theme?.colors?.accent }}
                >
                  <PenTool className="w-4 h-4 mr-2" />
                  Signer le brief
                </Button>
              )}
            </div>
          </div>

          {/* Signatures Status */}
          <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t">
            <div className={cn(
              "p-4 rounded-lg border-2",
              brief.agency_signed ? "border-green-200 bg-green-50" : "border-dashed border-[#e2e8f0]"
            )}>
              <div className="flex items-center gap-2 mb-2">
                {brief.agency_signed ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-[#64748b]" />
                )}
                <span className="font-medium" style={{ color: theme?.colors?.primary }}>
                  Signature Agence
                </span>
              </div>
              {brief.agency_signed ? (
                <p className="text-sm text-green-600">
                  Signé le {new Date(brief.agency_signature_date).toLocaleDateString('fr-FR')}
                </p>
              ) : (
                <p className="text-sm text-[#64748b]">En attente de signature</p>
              )}
            </div>

            <div className={cn(
              "p-4 rounded-lg border-2",
              brief.client_signed ? "border-green-200 bg-green-50" : "border-dashed border-[#e2e8f0]"
            )}>
              <div className="flex items-center gap-2 mb-2">
                {brief.client_signed ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-[#64748b]" />
                )}
                <span className="font-medium" style={{ color: theme?.colors?.primary }}>
                  Signature Client
                </span>
              </div>
              {brief.client_signed ? (
                <p className="text-sm text-green-600">
                  Signé le {new Date(brief.client_signature_date).toLocaleDateString('fr-FR')}
                </p>
              ) : (
                <p className="text-sm text-[#64748b]">En attente de signature</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Budget Section - Only show after both signed */}
      {isBothSigned && (
        <Card className="card border-2" style={{ borderColor: theme?.colors?.accent }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#64748b] mb-1">Budget validé</p>
                <p className="text-3xl font-mono font-bold" style={{ color: theme?.colors?.primary }}>
                  {((project?.estimated_hours || 0) * (project?.hourly_rate || 75)).toLocaleString('fr-FR')}€
                </p>
                <p className="text-sm text-[#64748b]">
                  {project?.estimated_hours || 0} heures × {project?.hourly_rate || 75}€/h
                </p>
              </div>
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${theme?.colors?.accent}20` }}
              >
                <FileText className="w-8 h-8" style={{ color: theme?.colors?.accent }} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Brief Content */}
      <Card className="card">
        <CardHeader>
          <CardTitle style={{ color: theme?.colors?.primary }}>
            Cahier des charges
          </CardTitle>
          <CardDescription>
            Créé le {new Date(brief.created_at).toLocaleDateString('fr-FR')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {brief.fields?.map((field, index) => (
              <div key={index} className="border-b border-[#e2e8f0] pb-4 last:border-0">
                <label className="text-sm font-medium text-[#64748b] block mb-1">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div className="mt-1">
                  {field.field_type === 'checkbox' ? (
                    <Badge className={field.value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {field.value ? 'Oui' : 'Non'}
                    </Badge>
                  ) : field.field_type === 'textarea' ? (
                    <p className="text-[#1a365d] whitespace-pre-wrap">
                      {field.value || <span className="text-[#94a3b8] italic">Non renseigné</span>}
                    </p>
                  ) : (
                    <p className="font-medium" style={{ color: theme?.colors?.primary }}>
                      {field.value || <span className="text-[#94a3b8] italic">Non renseigné</span>}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BriefView;
